import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

import { Box, Flex, LinkBox, LinkOverlay, Text } from '@chakra-ui/react';

import { trackEvent } from '~/features/analytics/track-event';
import { TagIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { SanitizedPromotion } from '~/features/promotions/models/promotions';

import { DEFAULT_OVERLAY_TEXT } from '../constants';

import { shouldShowPromotionOverlay } from './plan-helpers';

export function getCoverHeight({
  collapseWhenSticky = true,
  expandedHeight = 200,
  isMobile,
  isScrolled,
}: {
  collapseWhenSticky?: boolean;
  expandedHeight?: number;
  isMobile: boolean;
  isScrolled: boolean;
}) {
  return !isMobile && collapseWhenSticky && isScrolled ? 90 : expandedHeight;
}

export function isIntegratedCardPinned({ cardTop, stickyTop }: { cardTop: number; stickyTop: number }) {
  return Number.isFinite(stickyTop) && cardTop <= stickyTop + 1;
}

export function CoverWithOverlay({
  collapseWhenSticky = true,
  expandedHeight = 200,
  imageUrl,
  isMobile,
  promotion,
  showPromotionOverlay = true,
}: {
  collapseWhenSticky?: boolean;
  expandedHeight?: number;
  imageUrl: string | null;
  isMobile: boolean;
  promotion: SanitizedPromotion | undefined;
  showPromotionOverlay?: boolean;
}) {
  const coverRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const showOverlay = showPromotionOverlay && shouldShowPromotionOverlay(promotion);
  const height = getCoverHeight({ collapseWhenSticky, expandedHeight, isMobile, isScrolled });
  const overlay = promotion?.heroSticker ?? promotion?.priceCardsBanner;
  const overlayText = overlay?.text ?? DEFAULT_OVERLAY_TEXT;

  useEffect(() => {
    if (isMobile || !collapseWhenSticky) return;
    const updateScrolledState = () => {
      const stickyCard = coverRef.current?.parentElement;
      if (!stickyCard) return;
      const stickyTop = Number.parseFloat(window.getComputedStyle(stickyCard).top);
      const cardTop = stickyCard.getBoundingClientRect().top;
      setIsScrolled(isIntegratedCardPinned({ cardTop, stickyTop }));
    };
    updateScrolledState();
    window.addEventListener('scroll', updateScrolledState, { passive: true });
    window.addEventListener('resize', updateScrolledState);
    return () => {
      window.removeEventListener('scroll', updateScrolledState);
      window.removeEventListener('resize', updateScrolledState);
    };
  }, [collapseWhenSticky, isMobile]);

  function trackOverlayClick() {
    trackEvent({
      label: 'hero_sticker_clicked',
      name: 'Product Page CTA Clicked',
      type: 'click',
      value: promotion?.promotionId,
    });
  }

  const overlayContent = (
    <LinkBox
      alignItems='center'
      as={Flex}
      backdropFilter='blur(5px)'
      bg='black'
      borderRadius='6px'
      color='white'
      gap={2}
      left='11px'
      padding={3}
      position='absolute'
      right='11px'
      top={height === 90 ? '8px' : `${height - 83}px`}
      transition='top 180ms ease'
    >
      <TagIcon flex='0 0 16px' height='16px' width='16px' />
      <Text fontSize='13px' fontWeight='400' lineHeight='1.4'>
        {overlayText}
        {overlay?.linkUrl && overlay.linkLabel && (
          <>
            {' '}
            <LinkOverlay
              fontWeight='400'
              href={overlay.linkUrl}
              textDecoration='underline'
              textUnderlineOffset='3px'
              onClick={trackOverlayClick}
            >
              {overlay.linkLabel}
            </LinkOverlay>
          </>
        )}
      </Text>
    </LinkBox>
  );

  return (
    <Box
      ref={coverRef}
      bg='black'
      borderTopRadius='12px'
      data-testid='price-card-cover'
      height={`${height}px`}
      overflow='hidden'
      position='relative'
      transition='height 180ms ease'
      width='100%'
    >
      {imageUrl && (
        <Image alt='' quality={80} sizes='356px' src={imageUrl} style={{ objectFit: 'cover' }} fill priority />
      )}
      {showOverlay && overlayContent}
    </Box>
  );
}
